import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormActionBar, Panel, PanelSection, WindowContent, toast } from '@/shared/ui';
import { SegmentedField, TextField } from '@/shared/forms';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { getGetProductsPlaceholderSubcategoryQueryKey } from '@/api/generated/products-placeholder-subcategories/products-placeholder-subcategories';
import { PLACEHOLDER_SUBCATEGORY_LIST_KEY } from '../internal/schemesQueryKeys';
import { placeholderStatusOptions, placeholderTypeOptions } from '../internal/placeholderEnums';
import { FORM_LABEL_WIDTH } from '../internal/formLayout';
import { useSubcategoryForm } from './useSubcategoryForm';
import type { SubcategoryFormValues } from './subcategoryFormSchema';

export interface SubcategoryDetailViewProps {
  layerId: string;
  subcategoryId?: string;
  initialValues: SubcategoryFormValues;
}

/**
 * Orchestrátor detailu subkategorie placeholderu — drží formulář (`useSubcategoryForm`), skládá
 * pole (typ, název, kód, stav) a sdílenou lištu akcí v patičce.
 */
export function SubcategoryDetailView({
  layerId,
  subcategoryId,
  initialValues,
}: SubcategoryDetailViewProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const isEdit = subcategoryId != null;

  const close = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form, submit } = useSubcategoryForm({
    subcategoryId,
    initialValues,
    onSaved: (id) => {
      void queryClient.invalidateQueries({
        queryKey: getGetProductsPlaceholderSubcategoryQueryKey(id),
      });
      void queryClient.invalidateQueries({ queryKey: [PLACEHOLDER_SUBCATEGORY_LIST_KEY] });
      toast({ severity: 'success', description: t('schemes.subcategory.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('schemes.subcategory.toast.errorTitle'),
        description: t('schemes.subcategory.toast.saveError'),
      });
    },
  });

  const title = isEdit ? t('schemes.subcategory.titleEdit') : t('schemes.subcategory.titleCreate');

  return (
    <WindowContent
      as="form"
      width="reading"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      footer={
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ canSubmit, isSubmitting }) => (
            <FormActionBar
              onBack={close}
              onSave={submit}
              saving={isSubmitting}
              disabled={!canSubmit}
            />
          )}
        </form.Subscribe>
      }
    >
      <Panel variant="filled" title={title}>
        <PanelSection>
          <form.Field name="type">
            {(field) => (
              <SegmentedField
                field={field}
                label={t('schemes.subcategoryForm.type')}
                labelWidth={FORM_LABEL_WIDTH}
                options={placeholderTypeOptions(t)}
                testId="subcategory-type"
              />
            )}
          </form.Field>

          <form.Field name="name">
            {(field) => (
              <TextField
                field={field}
                label={t('schemes.subcategoryForm.name')}
                layout="inline"
                labelWidth={FORM_LABEL_WIDTH}
                required
                data-testid="subcategory-name"
              />
            )}
          </form.Field>

          <form.Field name="code">
            {(field) => (
              <TextField
                field={field}
                label={t('schemes.subcategoryForm.code')}
                layout="inline"
                labelWidth={FORM_LABEL_WIDTH}
                data-testid="subcategory-code"
              />
            )}
          </form.Field>

          <form.Field name="status">
            {(field) => (
              <SegmentedField
                field={field}
                label={t('schemes.subcategoryForm.status')}
                labelWidth={FORM_LABEL_WIDTH}
                options={placeholderStatusOptions(t)}
                testId="subcategory-status"
              />
            )}
          </form.Field>
        </PanelSection>
      </Panel>
    </WindowContent>
  );
}
